import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { Assignment } from "../api";
import { t } from "../i18n";
import { useModalKeys } from "./Modal";

// A share's assignment, chosen in the page (RFC-043 model 19): from a row's
// ⋯ menu, a dialog as the page's other questions are (EditTitle, Confirm) —
// the class's open assignments and none, the one it has picked; Save sends
// it, Esc or a click outside leaves it as it was.

type Choosing = { label: string; value: string; assignments: readonly Assignment[]; many: boolean; resolve: (key: string | null | undefined) => void };

/**
 * `[choose, dialog]`: `choose(label, current, assignments)` resolves
 * to the key picked, null for no assignment, or undefined when nothing
 * changed; `dialog` is the element to render, nothing while none is open.
 * `many`, it is for several shares at once, which have no one current
 * assignment: any choice is saved, none included.
 */
export function useChooseAssignment(): [
  (label: string, current: string, assignments: readonly Assignment[], many?: boolean) => Promise<string | null | undefined>,
  ReactNode,
] {
  const [choosing, setChoosing] = useState<Choosing | null>(null);
  const choose = useCallback(
    (label: string, value: string, assignments: readonly Assignment[], many = false) =>
      new Promise<string | null | undefined>((resolve) => setChoosing({ label, value, assignments, many, resolve })),
    [],
  );
  const done = useCallback(
    (key: string | null | undefined) => {
      choosing?.resolve(key);
      setChoosing(null);
    },
    [choosing],
  );
  return [choose, choosing ? <ChooseDialog choosing={choosing} onDone={done} /> : null];
}

function ChooseDialog({ choosing, onDone }: { choosing: Choosing; onDone: (key: string | null | undefined) => void }) {
  const [value, setValue] = useState(choosing.value);
  const select = useRef<HTMLSelectElement>(null);
  const open = choosing.assignments.filter((a) => !a.archived_at);

  const keys = useModalKeys(() => onDone(undefined));
  useEffect(() => {
    select.current?.focus();
  }, []);

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onDone(undefined)}>
      <form
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="choose-assignment-heading"
        onKeyDown={keys}
        onSubmit={(e) => {
          e.preventDefault();
          if (choosing.many || value !== choosing.value) onDone(value === "" ? null : value);
        }}
      >
        <h3 id="choose-assignment-heading">{t("classes:setAssignmentHeading")}</h3>
        <label className="field">
          {t("classes:assignments")}
          <select ref={select} aria-label={choosing.label} value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">{t("classes:noAssignmentChip")}</option>
            {open.map((a) => (
              <option key={a.key} value={a.key}>
                {a.title} ({a.key})
              </option>
            ))}
            {/* an archived one it is still on stays named */}
            {choosing.value && !open.some((a) => a.key === choosing.value) && <option value={choosing.value}>{choosing.value}</option>}
          </select>
        </label>
        <div className="row modal-actions">
          <button type="button" onClick={() => onDone(undefined)}>
            {t("common:cancel")}
          </button>
          <button type="submit" className="primary" disabled={!choosing.many && value === choosing.value}>
            {t("common:save")}
          </button>
        </div>
      </form>
    </div>
  );
}
